import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle, Eye, ChatCircleText, Microphone, ChartLineUp, Stack, Question } from "@phosphor-icons/react";

const TYPES = [
  { id: "plant-automation",     label: "I need plant automation",       icon: Stack,          hint: "PLC, MES, line orchestration" },
  { id: "computer-vision",      label: "I need computer vision",        icon: Eye,            hint: "QC, count, identity, posture" },
  { id: "ai-assistant",         label: "I need an AI assistant",        icon: ChatCircleText, hint: "LLM copilot or voice bot" },
  { id: "dashboards-analytics", label: "I need dashboards / analytics", icon: ChartLineUp,    hint: "Command-center, KPIs" },
  { id: "custom-platform",      label: "I need a custom platform",      icon: Microphone,     hint: "Stack across capture → action" },
  { id: "not-sure",             label: "I'm not sure — help me scope",  icon: Question,       hint: "Start with a discovery call" },
];

const TIMELINES = ["This quarter", "Next quarter", "6 months", "Exploratory"];
const DEPLOYMENTS = ["On-prem / edge", "Cloud", "Hybrid", "TBD"];
const BUDGETS = ["< $50k", "$50k–$200k", "$200k–$500k", "$500k+", "Not defined yet"];

export default function Contact() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    project_type: "",
    timeline: "",
    deployment: "",
    budget: "",
    challenge: "",
    name: "",
    email: "",
    company: "",
    demo_link: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!data.email || !data.name || !data.challenge || !data.project_type) {
      toast.error("A few required fields are missing.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/project-router", data);
      setDone(true);
      toast.success("Routed. We'll reach you within one working day.");
    } catch (e) {
      toast.error("Could not submit. Try again or email contact@wisewit.ai.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="router-done" className="px-6 md:px-14 py-24 md:py-32 min-h-[70vh]">
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl">
          <div>
            <CheckCircle size={42} color="#FF4500" weight="duotone" />
            <h1 className="mt-6 font-display tracking-tighter text-4xl md:text-5xl">Routed.</h1>
            <p className="mt-4 text-[#8E8E93] text-[15px] leading-relaxed">
              Your brief is in the queue. A senior engineer from the relevant capability will reach out
              within one working day, with concrete next steps and a few sharper questions.
            </p>
            <div className="mt-8 font-mono text-[10px] tracking-[0.2em] uppercase text-[#555]">
              Reference: WW-{Math.random().toString(36).slice(2, 8).toUpperCase()}
            </div>

            <div className="mt-12 ww-card p-6">
              <div className="text-overline mb-2">◢ Want it faster?</div>
              <h3 className="font-display text-2xl text-white">Book a 30-minute discovery call.</h3>
              <p className="mt-2 text-[#8E8E93] text-sm">
                Skip the queue — grab a slot on a senior engineer's calendar right now.
              </p>
              <a
                href={(process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/wisewit/discovery") + "?hide_event_type_details=1&hide_gdpr_banner=1"}
                target="_blank"
                rel="noreferrer"
                data-testid="calendly-cta"
                className="btn-primary mt-5 inline-flex"
              >
                Book a discovery call →
              </a>
            </div>
          </div>

          <div className="ww-card p-2 min-h-[640px]">
            <iframe
              data-testid="calendly-embed"
              title="Book a Wisewit discovery call"
              src={(process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/wisewit/discovery") + "?hide_event_type_details=1&hide_gdpr_banner=1&background_color=050505&text_color=f5f5f5&primary_color=ff4500"}
              width="100%"
              height="640"
              frameBorder="0"
              style={{ minHeight: 640, border: 0 }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="px-6 md:px-14 py-16 md:py-24">
      <div className="max-w-4xl">
        <div className="text-overline mb-4">◢ Project router</div>
        <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]">
          Tell us where it <span className="text-[#FF4500]">hurts</span>.
        </h1>
        <p className="mt-5 text-[#8E8E93] max-w-2xl text-[15px]">
          A short, adaptive routing wizard. Picks the right team and the right opening question.
        </p>
      </div>

      {/* Progress */}
      <div className="mt-12 max-w-4xl flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[#8E8E93]">
        {["Type", "Context", "Brief", "You"].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <span className={`w-6 h-6 flex items-center justify-center border ${i <= step ? "border-[#FF4500] text-white" : "border-[#2A2A2A] text-[#555]"}`}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className={i <= step ? "text-white" : ""}>{label}</span>
            {i < 3 && <span className="text-[#2A2A2A]">───</span>}
          </div>
        ))}
      </div>

      <div className="mt-10 max-w-4xl ww-card p-6 md:p-10" data-testid="router-wizard">
        {step === 0 && (
          <div data-testid="step-type">
            <label className="ww-label">01 · Project type</label>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {TYPES.map((t) => {
                const Icon = t.icon;
                const active = data.project_type === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`type-${t.id}`}
                    onClick={() => set("project_type", t.id)}
                    className={`text-left p-5 border transition-all flex items-start gap-4 ${
                      active ? "border-[#FF4500] bg-[#FF4500]/5" : "border-[#2A2A2A] hover:border-[#404040]"
                    }`}
                  >
                    <Icon size={22} color={active ? "#FF4500" : "#00E5FF"} weight="duotone" />
                    <div>
                      <div className="font-display text-lg text-white">{t.label}</div>
                      <div className="text-xs text-[#8E8E93] mt-1">{t.hint}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="step-context" className="space-y-8">
            <div>
              <label className="ww-label">02a · Timeline</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {TIMELINES.map((t) => <Chip key={t} active={data.timeline === t} onClick={() => set("timeline", t)} label={t} testid={`timeline-${t}`}/>)}
              </div>
            </div>
            <div>
              <label className="ww-label">02b · Deployment</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {DEPLOYMENTS.map((d) => <Chip key={d} active={data.deployment === d} onClick={() => set("deployment", d)} label={d} testid={`deploy-${d}`}/>)}
              </div>
            </div>
            <div>
              <label className="ww-label">02c · Budget</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {BUDGETS.map((b) => <Chip key={b} active={data.budget === b} onClick={() => set("budget", b)} label={b} testid={`budget-${b}`}/>)}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-brief" className="space-y-6">
            <div>
              <label className="ww-label">03a · Current challenge</label>
              <textarea
                data-testid="challenge-input"
                value={data.challenge}
                onChange={(e) => set("challenge", e.target.value)}
                rows={6}
                placeholder="Describe the problem on the floor, the line, or the dashboard. Numbers help."
                className="ww-input mt-2 resize-none"
              />
            </div>
            <div>
              <label className="ww-label">03b · Demo / docs / repo (optional)</label>
              <input
                data-testid="demo-input"
                value={data.demo_link}
                onChange={(e) => set("demo_link", e.target.value)}
                placeholder="https://"
                className="ww-input"
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-you" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="ww-label">Name</label>
                <input data-testid="name-input" value={data.name} onChange={(e) => set("name", e.target.value)} className="ww-input" placeholder="Your name" />
              </div>
              <div>
                <label className="ww-label">Work email</label>
                <input data-testid="email-input" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className="ww-input" placeholder="you@operation.com" />
              </div>
            </div>
            <div>
              <label className="ww-label">Company</label>
              <input data-testid="company-input" value={data.company} onChange={(e) => set("company", e.target.value)} className="ww-input" placeholder="Optional" />
            </div>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <button onClick={back} disabled={step === 0} data-testid="back-btn" className="btn-ghost disabled:opacity-30">
            <ArrowLeft size={14} /> Back
          </button>
          {step < 3 ? (
            <button
              onClick={next}
              disabled={(step === 0 && !data.project_type) || (step === 2 && !data.challenge)}
              data-testid="next-btn"
              className="btn-primary disabled:opacity-50"
            >
              Next <ArrowRight size={14} weight="bold" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="submit-btn" className="btn-primary">
              {submitting ? "Routing…" : <>Route my project <ArrowRight size={14} weight="bold" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`font-mono text-[11px] tracking-[0.14em] uppercase px-3 py-2 border transition-all ${
        active ? "border-[#FF4500] bg-[#FF4500]/10 text-white" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white hover:border-[#404040]"
      }`}
    >
      {label}
    </button>
  );
}
